import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantContext } from '@/auth/TenantContext';
import { useSignupTenant } from '@/lib/api/tenants';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';

/**
 * Membership gate for the authenticated SPA (spec 0013). Blocks on the initial
 * membership load and on a load error; if the user belongs to no workspace it
 * shows the create-workspace onboarding. Once the caller has a membership it
 * renders its children -- the active workspace is derived from the URL by
 * `WorkspaceLayout`, so this layer no longer gates on the active tenant.
 */
export function RequireTenant({ children }: { children: ReactNode }) {
  const { tenants, isLoading, loadError, reload } = useTenantContext();
  const navigate = useNavigate();
  const signup = useSignupTenant();
  const [name, setName] = useState('');

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <Spinner label="Loading workspaces" />
      </div>
    );
  }

  // A failed membership lookup is not "no workspaces": show the error instead
  // of the create-workspace onboarding (e.g. backend asleep -> 503).
  if (loadError) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <Card className="w-full max-w-md">
          <h1 className="text-lg font-semibold text-gray-900">Cannot load your workspaces</h1>
          <p className="mt-2 text-sm text-gray-600">
            The server did not respond. It may be waking up or offline; try again in a moment.
          </p>
          <p className="mt-2 text-sm text-red-600">{loadError.message}</p>
          <Button className="mt-4" onClick={reload}>
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  if (tenants.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <Card className="w-full max-w-md">
          <h1 className="text-lg font-semibold text-gray-900">Create your workspace</h1>
          <p className="mt-2 text-sm text-gray-600">
            You do not belong to any workspace yet. Create one to get started.
          </p>
          <form
            className="mt-4 space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!name.trim()) return;
              signup.mutate(
                { name: name.trim() },
                {
                  // Land in the newly created workspace's slug prefix (spec 0013).
                  onSuccess: (membership) => navigate(`/${membership.tenant.slug}`),
                },
              );
            }}
          >
            <Input
              aria-label="Workspace name"
              placeholder="Workspace name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              data-testid="workspace-name-input"
            />
            {signup.isError && (
              <p className="text-sm text-red-600" data-testid="workspace-error">
                {(signup.error as Error).message}
              </p>
            )}
            <Button
              type="submit"
              disabled={signup.isPending || !name.trim()}
              data-testid="create-workspace-button"
            >
              {signup.isPending ? 'Creating...' : 'Create workspace'}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
